import { STAFF, qk, useApi, useApiMutation, useApiQuery } from '@legalcrm/api';
import { cn } from '@legalcrm/ui';
import { NavLink } from 'react-router-dom';

import { useAuth } from '@/auth/AuthProvider';

import { NavGlyph } from './icons';
import { visibleNavigation } from './navigation';

export interface SidebarProps {
  /** Mobile only. The sidebar is always present from `lg` up. */
  open: boolean;
  onNavigate: () => void;
}

/**
 * How often the dots refresh.
 *
 * A minute, not the five seconds the conversation screen polls at. A dot is an ambient
 * signal -- nobody sits watching the sidebar for one to appear -- and this request rides
 * on every page in the application, so its cost is paid everywhere.
 */
const SUMMARY_POLL_MS = 60_000;

export function Sidebar({ open, onNavigate }: SidebarProps) {
  const { permissions, me } = useAuth();
  const api = useApi();
  const sections = visibleNavigation(permissions);

  /**
   * Unread notifications per area, which is what the dots are.
   *
   * Derived from this person's own notifications rather than from "has anything changed
   * here", and the difference matters: a dot appears only for something they were
   * actually told about, so it can never point at a matter they are not on. Activity
   * they have no part in is not their business and does not light up their sidebar.
   */
  // Its OWN cache key. `qk.notifications()` already belongs to the bell's list, and
  // sharing it served this object to a component expecting a page -- `data.results` was
  // undefined and the whole application crashed on `.filter`. Two queries with one key
  // must return one shape, and TypeScript cannot see across a cache to tell you they do
  // not.
  const summary = useApiQuery<Record<string, number>>(
    qk.list('notifications', { view: 'summary' }),
    STAFF.notificationsSummary,
    { refetchInterval: SUMMARY_POLL_MS },
  );

  const clearArea = useApiMutation<{ marked_read: number }, string>(
    {
      mutationFn: (area: string) =>
        api.post(`${STAFF.notificationsReadArea}/`, { area }),
      // Both: the dots and the bell are two views of the same rows.
      invalidates: [qk.list('notifications', { view: 'summary' }), qk.notifications()],
    },
    {},
  );

  return (
    <>
      {/* Mobile scrim. Not focusable: the drawer traps focus itself. */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-scrim lg:hidden"
          onClick={onNavigate}
          aria-hidden="true"
        />
      )}

      <nav
        aria-label="Main"
        className={cn(
          // The navy band the client app puts the firm's name on, so staff and clients
          // are plainly using one product.
          'fixed inset-y-0 left-0 z-40 flex w-60 flex-col border-r border-hero-line bg-hero',
          'transition-transform lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-14 items-center gap-2.5 border-b border-hero-line px-4">
          {/* The firm mark from the client app: initials in Fraunces on a tile, with
              the brass rule under them. */}
          <span
            className="relative flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-sm bg-hero-line pb-0.5 font-display text-[13px] font-semibold tracking-wide text-hero-ink"
            aria-hidden="true"
          >
            {monogramFor(me?.organization?.name ?? 'Legal CRM')}
            <span className="absolute bottom-1.5 left-2.5 right-2.5 h-0.5 rounded-full bg-accent" />
          </span>
          <span className="truncate text-base font-semibold text-hero-ink">
            {me?.organization?.name ?? 'Legal CRM'}
          </span>
        </div>

        <div className="flex-1 overflow-y-auto px-2 py-3">
          {sections.map((section) => (
            <div key={section.key} className="mb-4 last:mb-0">
              {section.label && (
                <h2 className="px-2 pb-1.5 text-xs font-semibold uppercase tracking-wide text-hero-muted/80">
                  {section.label}
                </h2>
              )}
              <ul className="flex flex-col gap-0.5">
                {section.items.map((item) => {
                  const unread = summary.data?.[item.key] ?? 0;
                  return (
                  <li key={item.key}>
                    <NavLink
                      to={item.to}
                      // `end` on the dashboard only, or "/" matches every route.
                      end={item.to === '/'}
                      onClick={() => {
                        // Going to look at it is reading it. Only this area's dot
                        // clears: opening Cases says nothing about your invoices.
                        if (unread > 0) clearArea.mutate(item.key);
                        onNavigate();
                      }}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-2.5 rounded-sm px-2 py-1.5 text-base transition-colors',
                          isActive
                            ? 'bg-hero-line font-medium text-hero-ink'
                            : 'text-hero-muted hover:bg-hero-line/60 hover:text-hero-ink',
                        )
                      }
                    >
                      <NavGlyph name={item.icon} />
                      <span className="flex-1">{item.label}</span>
                      {unread > 0 && (
                        <span
                          // A dot, not a count. The number is not the point -- "there is
                          // something here" is -- and a count invites reading it as a
                          // queue length, which it is not.
                          className="h-2 w-2 shrink-0 rounded-full bg-accent"
                          aria-label={`${unread} unread`}
                        />
                      )}
                    </NavLink>
                  </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-hero-line px-4 py-2.5">
          <p className="truncate text-xs text-hero-muted">
            {me?.user.full_name} · {roleLabel(me?.role)}
          </p>
        </div>
      </nav>
    </>
  );
}

/**
 * "Ashford & Ross LLP" -> "A&R"; "Calder Vance" -> "CV"; "Northgate" -> "N". The same
 * mark the client app draws, so a firm is recognisable on both.
 */
export function monogramFor(name: string): string {
  const words = name
    .split(/\s+/)
    .filter((word) => word && !/^(llp|ltd|llc|inc|plc|&|and)$/i.test(word));
  const initials = words.slice(0, 2).map((word) => word[0]!.toUpperCase());
  if (initials.length === 2 && /\s(&|and)\s/i.test(name)) return initials.join('&');
  return initials.join('') || 'L';
}

const ROLE_LABELS: Record<string, string> = {
  org_admin: 'Organization Administrator',
  partner: 'Partner',
  lawyer: 'Lawyer',
  paralegal: 'Paralegal',
  account_executive: 'Account Executive',
  finance: 'Finance',
  intake: 'Intake',
  auditor: 'Auditor',
  external_counsel: 'External Counsel',
  platform_admin: 'Platform Administrator',
  platform_support: 'Platform Support',
};

function roleLabel(role: string | undefined): string {
  if (!role) return '';
  return ROLE_LABELS[role] ?? role;
}
